/**
 * Lesson media for Training (the LMS learner side). Mirrors AVS OS's shared/lesson-media.ts so a
 * lesson renders the same here as in the OS: the same embed allowlist, the same screenshot-path
 * rule. AVS OS already validates both when a lesson is saved; this is the second check, because an
 * iframe pointed at an arbitrary origin is a way to run someone else's page inside Copilot.
 */

const ALLOWED_VIDEO_HOSTS = [
  "drive.google.com",
  "www.loom.com",
  "loom.com",
  "player.vimeo.com",
  "vimeo.com",
  "www.youtube.com",
  "youtube.com",
  "youtu.be",
  "www.youtube-nocookie.com",
  "youtube-nocookie.com",
];

function parseHttps(url: string | null | undefined): URL | null {
  if (!url) return null;
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === "https:" ? parsed : null;
  } catch {
    return null;
  }
}

/** The embeddable URL for a shared video link, or null when it is not on the allowlist. */
export function toEmbedUrl(url: string | null | undefined): string | null {
  const parsed = parseHttps(url);
  if (!parsed) return null;
  const rawHost = parsed.hostname.toLowerCase();
  if (!ALLOWED_VIDEO_HOSTS.includes(rawHost)) return null;
  const host = rawHost.replace(/^www\./, "");

  if (host === "drive.google.com") {
    const m = parsed.pathname.match(/^\/file\/d\/([^/]+)/);
    return m ? `https://drive.google.com/file/d/${m[1]}/preview` : null;
  }
  if (host === "loom.com") {
    const m = parsed.pathname.match(/^\/(?:share|embed)\/([^/?]+)/);
    return m ? `https://www.loom.com/embed/${m[1]}` : null;
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const m = parsed.pathname.match(/(\d+)/);
    return m ? `https://player.vimeo.com/video/${m[1]}` : null;
  }
  if (host === "youtu.be") {
    const id = parsed.pathname.replace(/^\//, "");
    return id ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}` : null;
  }
  if (host === "youtube.com" || host === "youtube-nocookie.com") {
    const id = parsed.searchParams.get("v") ?? parsed.pathname.match(/^\/embed\/([^/?]+)/)?.[1] ?? null;
    return id ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}` : null;
  }
  return null;
}

const RELATIVE_PATH_RE = /^[a-zA-Z0-9_\-./]+$/;

/** A step screenshot: a bucket-relative path joined to the KB image prefix AVS OS sends. */
export function resolveImageUrl(path: string | null | undefined, prefix: string | null | undefined): string | null {
  if (!path || !prefix) return null;
  const trimmed = path.trim();
  if (!trimmed || trimmed.includes("://") || trimmed.startsWith("//")) return null;
  const rel = trimmed.replace(/^\/+/, "");
  if (!rel || rel.split("/").includes("..") || !RELATIVE_PATH_RE.test(rel)) return null;
  const base = parseHttps(prefix);
  if (!base) return null;
  return `${prefix.replace(/\/+$/, "")}/${rel}`;
}

/** The furthest point played, as a whole percent. Never moves backwards. */
export function watchedPercent(previous: number, currentTime: number, duration: number): number {
  if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(currentTime)) return previous;
  const now = Math.floor((Math.max(0, currentTime) / duration) * 100);
  return Math.max(previous, Math.min(100, now));
}

export function formatMinutes(minutes: number | null | undefined): string | null {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

export function formatDue(due: string | null | undefined): string | null {
  if (!due) return null;
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
